import React from "react";
import { NavLink, Link, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Megaphone, FileText, ShieldCheck, Building2,
  ClipboardList, Users, BarChart3, PlusCircle, LogOut, Pickaxe, Home, Activity, UserX,
} from "lucide-react";
import { toast } from "sonner";

const NAV = {
  smme: [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, id: "nav-dashboard" },
    { to: "/app/profile", label: "Company Profile", icon: Building2, id: "nav-profile" },
    { to: "/app/opportunities", label: "Opportunities", icon: FileText, id: "nav-opportunities" },
    { to: "/app/compliance", label: "Compliance Documents", icon: ShieldCheck, id: "nav-compliance" },
    { to: "/app/responses", label: "My Responses", icon: ClipboardList, id: "nav-responses" },
    { to: "/app/notice-board", label: "Notice Board", icon: Megaphone, id: "nav-notice-board" },
  ],
  buyer: [
    { to: "/app/post", label: "Post Opportunity", icon: PlusCircle, id: "nav-post" },
    { to: "/app/smme-database", label: "SMME Database", icon: Users, id: "nav-smme-db" },
    { to: "/app/reports", label: "Reports", icon: BarChart3, id: "nav-reports" },
    { to: "/app/opportunities", label: "My Opportunities", icon: FileText, id: "nav-opportunities" },
    { to: "/app/notice-board", label: "Notice Board", icon: Megaphone, id: "nav-notice-board" },
  ],
  admin: [
    { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, id: "nav-dashboard" },
    { to: "/app/users", label: "User Accounts", icon: Users, id: "nav-users" },
    { to: "/app/smme-database", label: "SMME Database", icon: Building2, id: "nav-smme-db" },
    { to: "/app/reports", label: "Reports", icon: BarChart3, id: "nav-reports" },
    { to: "/app/traffic", label: "Site Traffic", icon: Activity, id: "nav-traffic" },
    { to: "/app/orphans", label: "Orphan Accounts", icon: UserX, id: "nav-orphans" },
    { to: "/app/post", label: "Post Opportunity", icon: PlusCircle, id: "nav-post" },
    { to: "/app/opportunities", label: "Opportunities", icon: FileText, id: "nav-opportunities" },
    { to: "/app/notice-board", label: "Notice Board", icon: Megaphone, id: "nav-notice-board" },
  ],
};

const ROLE_LABEL = { smme: "SMME Supplier", buyer: "Mine Buyer", admin: "Administrator" };

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = NAV[user?.role] || [];

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/");
  };

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-72 flex-col bg-[#1E252B] text-slate-200 fixed h-full z-40">
        <div className="px-6 py-6 border-b border-white/10 flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-[#F2B50C] flex items-center justify-center">
            <Pickaxe className="h-5 w-5 text-[#0A2540]" />
          </div>
          <div>
            <p className="font-display font-extrabold text-white leading-none text-lg tracking-tight">ASSMANG</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-1">SMME Portal</p>
          </div>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-[#00427A] text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <it.icon className="h-[18px] w-[18px]" />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-white/10">
          <div className="px-2 mb-3">
            <p className="text-sm font-semibold text-white truncate">{user?.name} {user?.surname}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            <span className="inline-block mt-2 text-[10px] uppercase tracking-wider font-mono-data bg-[#F2B50C]/20 text-[#FCD34D] px-2 py-0.5 rounded">
              {ROLE_LABEL[user?.role]}{user?.mine_name ? ` · ${user.mine_name}` : ""}
            </span>
          </div>
          <Link
            to="/"
            data-testid="nav-home-button"
            className="w-full flex items-center gap-2 px-4 py-2.5 mb-1 rounded-md text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Home className="h-4 w-4" /> Home
          </Link>
          <button
            data-testid="nav-logout-button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2.5 rounded-md text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-[#1E252B] text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Pickaxe className="h-5 w-5 text-[#F2B50C]" />
          <span data-testid="topbar-user-name-mobile" className="font-display font-bold text-sm truncate max-w-[160px]">{user?.name} {user?.surname}</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" data-testid="nav-home-button-mobile" aria-label="Home"><Home className="h-5 w-5" /></Link>
          <button data-testid="nav-logout-button-mobile" onClick={handleLogout}>
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>

      <main className="flex-1 lg:ml-72 pt-16 lg:pt-0 min-h-screen">
        {/* Desktop top welcome bar */}
        <div className="hidden lg:flex items-center justify-between bg-white border-b border-slate-200 px-8 py-3.5 sticky top-0 z-30">
          <p className="text-sm text-slate-500">
            Signed in as{" "}
            <span data-testid="topbar-user-name" className="font-semibold text-slate-900">{user?.name} {user?.surname}</span>
          </p>
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-[#00427A] bg-[#00427A]/10 px-3 py-1.5 rounded-full">
            {ROLE_LABEL[user?.role]}{user?.mine_name ? ` · ${user.mine_name}` : ""}
          </span>
        </div>
        {/* Mobile nav scroll */}
        <div className="lg:hidden overflow-x-auto bg-white border-b border-slate-200 px-3 py-2 flex gap-1 sticky top-16 z-30">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end}
              className={({ isActive }) =>
                `whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium ${
                  isActive ? "bg-[#00427A] text-white" : "text-slate-600 bg-slate-100"
                }`}>
              {it.label}
            </NavLink>
          ))}
        </div>
        <div className="p-5 sm:p-8 max-w-7xl mx-auto animate-fade-up">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
